import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { Button, Well } from 'react-bootstrap';

import { IObjektListeResponse, IVorgang, IVorgangVorlage } from '../../models/ApiTypes';
import { UiStore } from '../../stores/UiStore';
import { bind, inject } from '../../utils/decorators';
import ArbeitsmittelListe from '../arbeitsmittel/ArbeitsmittelListe';
import Callout from '../site/Callout';
import DocumentList from '../dokumente/DocumentList';
import VorlagenBereich from '../vorlagen/VorlagenBereich';
import { AuftragStore } from '../../stores/AuftragStore';
import ObjektListe from '../objekte/ObjektListe';
import SesAnzeigeSystemneigung from '../vorlagen/ses/SesAnzeigeSystemneigung';
import ThumbnailList from '../dokumente/ThumbnailList';
import VorgangFormularEingaben from './VorgangFormularEingaben';
import { ApiStore } from '../../stores/ApiStore';
import { Link } from 'react-router-dom';
import VorgangUebermittlungInfo from '../vorlagen/VorgangUebermittlungInfo';
import VorlagenBereichReadOnly from '../vorlagen/VorlagenBereichReadOnly';

interface IVorgangDetailsProps {
  vorgang: IVorgang;
  vorlage?: IVorgangVorlage;
  objekte: IObjektListeResponse;
  onKorrekturvorgangAnlegen: (vorgangId: string) => void;
  onObjektAuswahlAufheben: (vorgangId: IVorgang) => void;
}

@observer
export default class VorgangDetails extends React.Component<IVorgangDetailsProps, {}> {

  @observable docList: DocumentList;
  @observable arbeitsmittelListe: ArbeitsmittelListe;
  // True wenn auf der Datenbank alle zum Vorgang einzugebenden Formulardaten hinterlegt sind
  @observable formulardatenVollstaendig: boolean = false;
  @observable formulardatenHinweise: string[] = [];

  @inject(UiStore) uiStore: UiStore;
  @inject(AuftragStore) auftragStore: AuftragStore;
  @inject(ApiStore) apiStore: ApiStore;

  componentDidMount() {
    this.refreshFormulardatenVollstaendig();
  }

  @computed get auftragAbgeschlossen() {
    return (this.auftragStore.auftrag && this.auftragStore.auftrag.abgeschlossen);
  }

  @bind @action setArbeitsmittelListe(arbeitsmittelListe: ArbeitsmittelListe | null) {
    if (arbeitsmittelListe) {
      this.arbeitsmittelListe = arbeitsmittelListe;
    }
  }

  @bind @action setDocumentList(docList: DocumentList | null) {
    if (docList) {
      this.docList = docList;
    }
  }

  // Wird von der Komponente für die FormularEingaben aufgerufen
  // Entscheidet ob die Funktionen zum Erzeugen und Hochladen der Dokumente sichtbar sind
  @bind @action handleFormDataSubmitted() {
    this.refreshFormulardatenVollstaendig();
  }

  @action private setFormulardatenVollstaendig(value: boolean) {
    this.formulardatenVollstaendig = value;
  }

  @action private setFormulardatenHinweise(value: string[]) {
    this.formulardatenHinweise = value;
  }

  // Prüft ob alle erforderlichen Formulardaten auf Datenbank hinterlegt sind
  async refreshFormulardatenVollstaendig() {
    if (!this.props.vorgang.abgeschlossen) {
      const response = await this.apiStore.vorgangFormularEingabenVollstaendig(this.props.vorgang.vorgangguid);
      if (response.ok) {
        this.setFormulardatenVollstaendig(response.json.success === true);
        this.setFormulardatenHinweise(response.json.hinweise);
      }
    } else {
      this.setFormulardatenVollstaendig(true);
      this.setFormulardatenHinweise([]);
    }
  }

  @bind handleDocumentRefresh() {
    if (this.docList) {
      this.docList.refresh();
    }
  }

  @computed get requirementsFulfilled() {
    const vorlage = this.props.vorlage;
    if (vorlage && vorlage.formular_kategorien && vorlage.formular_kategorien.length > 0 && !this.formulardatenVollstaendig) {
        return false;
    }
    return (vorlage && (vorlage.auswahl_arbeitsmittel === 0 || (this.arbeitsmittelListe && this.arbeitsmittelListe.arbeitsmittel.length !== 0)));
  }

  render() {
    const { vorgang, vorlage, objekte, onKorrekturvorgangAnlegen, onObjektAuswahlAufheben } = this.props;
    const { abgeschlossen, vorgangguid } = vorgang;
    if (!(vorlage && vorlage.vorlage)) {
      return (
        <div className="maxwidth">
          <Well>Dieser Vorgang kann nicht bearbeitet werden.</Well>
          <DocumentList source="vorgang" id={vorgangguid} />
        </div>
      );
    }
    const vorlageBezeichnung = vorlage.vorlage.toLocaleLowerCase();
    const istDienstleistungsnachweis = (vorlage.vorlage.toLocaleLowerCase().indexOf('nachweis_dienstleistung') > -1);
    const vorgangSesVermessung = (vorlageBezeichnung === 'ses_auswertung_messwerte') || (vorlageBezeichnung === 'ses_uebernahme_messwerte') || (vorlageBezeichnung === 'ses_auswertung_messwerte_rili_2018');
    const documentListThumbnails = (vorlageBezeichnung.indexOf('foto_dokumentation') > -1);

    // Für diese Vorlagen wird ein separate Hinweis bzgl. der Freigabe durch die DIQ Zert angezeigt
    const vorlagenVorlaeufigeDatenUebermittlung = [
      'dakks_ses_stückprüfung_protokoll',
      'dakks_sep_kalibrierung_kalibrierschein',
      'dakks_ses_vermessung_kalibrierschein',
      'dakks_ses_vermessung_kalibrierschein_rili_2018',
      'dakks_ses_vermessung_kalibrierschein_rili_2018_2025',
      'bps_referenzkraft_kalibrierschein',
      'dakks_agt_4gas_kalibrierschein',
      'dakks_agt_opazimeter_kalibrierschein',
      'dakks_agt_partikelzaehler_kalibrierschein'
    ];

    return (
      <div className="vorgang-details">
        {vorlage.auswahl_arbeitsmittel === 1 && (
          <ArbeitsmittelListe ref={this.setArbeitsmittelListe} vorgangId={vorgangguid} editable={!abgeschlossen} />
        )}

        {vorlage.auswahl_objekte === 1 && (
          <ObjektListe
            objekte={objekte}
            auftrag={this.auftragStore.auftrag}
            vorgang={vorgang}
            editable={!abgeschlossen}
            onAuswahlAufheben={() => onObjektAuswahlAufheben(vorgang)}
          />
        )}

        {/* Eingabe Formulardaten (definiert über Vorlage) */}
        {vorlage && vorlage.formular_kategorien && vorlage.formular_kategorien.length > 0 && (
          <VorgangFormularEingaben
            vorgangId={vorgangguid}
            formular_kategorien={vorlage.formular_kategorien}
            formulardaten={vorgang.formulardaten}
            handleDocumentRefresh={this.handleDocumentRefresh}
            handleFormDataSubmitted={this.handleFormDataSubmitted}
            editable={!abgeschlossen}
          />
        )}

        {!abgeschlossen && !this.requirementsFulfilled && (
          <RequirementsInfo
            arbeitsmittel={(vorlage && vorlage.auswahl_arbeitsmittel > 0) ? (this.arbeitsmittelListe && this.arbeitsmittelListe.arbeitsmittel.length > 0) : true}
            formulareingaben={(vorlage.formular_kategorien && vorlage.formular_kategorien.length > 0) ? this.formulardatenVollstaendig : true}
            hinweise={this.formulardatenHinweise}
          />
        )}

        {!abgeschlossen && this.requirementsFulfilled && (
          <VorlagenBereich
            vorlagenTyp={vorlage.vorlage.toLowerCase()}
            vorgang={vorgang}
            handleDocumentRefresh={this.handleDocumentRefresh}
          />
        )}

        {abgeschlossen && <VorlagenBereichReadOnly
            vorlagenTyp={vorlage.vorlage.toLowerCase()}
            vorgang={vorgang}
          />
        }

        {istDienstleistungsnachweis && abgeschlossen && (
          <Callout bsStyle="info">
            <h4>Vorgang abgeschlossen</h4>
            <p>Die Informationen zu den von Ihnen erbrachten Dienstleistungen wurden an die DIQ Zert GmbH übermittelt.</p>
            <p>Die Rechnung an den Auftraggeber sowie Ihre Gutschrift (nach Abzug der Abgaben) werden nach Prüfung postalisch versendet.</p>
          </Callout>
        )}

        {/* Bei Vorgängen mit vorläufiger Datenübermittlung (neue ISO 17025:2018) entsprechenden Hinweis anzeigen */}
        {abgeschlossen && vorlagenVorlaeufigeDatenUebermittlung.indexOf(vorlageBezeichnung) > -1 && (
          <VorgangUebermittlungInfo
            vorgang={vorgang}
          />
        )}

        {/* Bei abgeschlossenen Vorgängen mit Messwerten SES Tabelle Systemneigung anzeigen  */}
        {vorgangSesVermessung && abgeschlossen && (
          <div>
            <SesAnzeigeSystemneigung
              vorgangId={vorgangguid}
            />
          </div>
        )}

        {!istDienstleistungsnachweis && (
          <div>
            {!documentListThumbnails && <DocumentList ref={this.setDocumentList} source="vorgang" id={vorgangguid} />}
            {documentListThumbnails && (<ThumbnailList id={vorgangguid} />)}

            {!this.auftragAbgeschlossen && abgeschlossen && onKorrekturvorgangAnlegen && (
              <Callout bsStyle="info">
                <h4>Dieser Vorgang ist abgeschlossen, ein Ändern der Daten ist nicht mehr möglich.</h4>
                <p>Die Daten können jedoch über einen Korrekturvorgang erneut (in korrigierter Form) erfasst werden.</p>
                <div className="text-right">
                  <Button bsStyle="info" type="submit" onClick={() => onKorrekturvorgangAnlegen(vorgangguid)}>Korrekturvorgang anlegen</Button>
                </div>
              </Callout>
            )}
          </div>
        )}
      </div>
    );
  }
}

interface IRequirementsInfoProps {
  arbeitsmittel: boolean;
  formulareingaben: boolean;
  hinweise: string[];
}

class RequirementsInfo extends React.Component<IRequirementsInfoProps, {}> {

  @inject(UiStore) uiStore: UiStore;

  render() {
    const { arbeitsmittel, formulareingaben, hinweise } = this.props;
    return (
      <div>
        {!formulareingaben && (
          <Callout bsStyle="info">
          <h4>Eingabedaten unvollständig</h4>
          <p>Es wurden noch nicht alle zum Erzeugen der Dokumente notwendigen Eingabedaten erfasst. Sobald die fehlenden Eingaben ergänzt und gespeichert wurden, können die Dokumente zu diesem Vorgang erzeugt werden.</p>
          {hinweise && <div>
            {hinweise.map((hinweis, index) => { return <p key={`req-hinweis-${index}`}>{hinweis}</p>; })}
            </div>
          }
        </Callout>
        )}
        {!arbeitsmittel && (
          <Callout bsStyle="info">
          <h4>Keine Arbeitsmittel gewählt</h4>
          <p>Zum Erzeugen der Dokumente ist eine Auswahl der verwendeten Arbeitsmittel notwendig. Bitte <Link to={`${this.uiStore.currentLocation.pathname}/arbeitsmittel`}>wählen Sie hier</Link> die für den Vorgang genutzten Arbeitsmittel aus.</p>
        </Callout>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorgaenge/VorgangDetails.tsx